/**
 * Authentication context and route guards. Holds the signed-in user (me),
 * exposes login/signup/logout, and derives the account's capability model
 * (SPEC.md "One person, multiple hats"): the canBuild / isParent / isMentor
 * flags, the hats to show in the top-nav switcher, and the currently active hat
 * (persisted across visits). Route guards gate by role, by capability, and by
 * account status (pending and paused users are redirected to their status
 * screens).
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import type {
  AuthResponse,
  Capabilities,
  Hat,
  LoginInput,
  Role,
  SessionUser,
  SignupInput,
} from '@proteam/shared';

import { ApiError, api } from './api.js';
import { getWs } from './ws.js';
import { readStoredHat, writeStoredHat } from './hatStorage.js';
import { resolveActiveHat } from '../app/hats.js';

export type AuthStatus = 'loading' | 'authed' | 'anon';

/**
 * Sign up, optionally naming the account's AI at the same time. Only the student
 * and adult flows collect a name; the field is stored on users.ai_name.
 */
export type SignupWithAiName = SignupInput & { aiName?: string };

/** A capability the app can gate a route on, mapped onto the account's hats. */
export type Capability = 'build' | 'parent' | 'mentor';

export interface AuthContextValue {
  me: SessionUser | null;
  status: AuthStatus;
  role: Role | null;
  /** The account's capabilities, or null when signed out. */
  capabilities: Capabilities | null;
  /** Can create projects and run sessions (any approved account, incl. grownups). */
  canBuild: boolean;
  /** Has linked kids, so the parent surfaces apply. */
  isParent: boolean;
  /** Mentor-role account, so the mentor surfaces apply. */
  isMentor: boolean;
  /** The hats to show in the switcher, in nav order. */
  hats: Hat[];
  /** The active hat, or null when signed out. */
  hat: Hat | null;
  /** Switch the active hat and remember the choice for next time. */
  setHat: (hat: Hat) => void;
  login: (input: LoginInput) => Promise<SessionUser>;
  signup: (input: SignupWithAiName) => Promise<SessionUser>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** The landing route for a signed-in user of each role (the role's default hat). */
export function roleHome(role: Role): string {
  switch (role) {
    case 'student':
      return '/build';
    case 'parent':
      return '/parent';
    case 'mentor':
      return '/mentor';
  }
}

/**
 * Pure guard decision. Given the current user and the roles a route allows,
 * returns the path to redirect to, or null to render the route. Callers handle
 * the loading state before calling this.
 */
export function guardRedirect(me: SessionUser | null, roles?: readonly Role[]): string | null {
  if (!me) return '/login';
  if (me.status === 'pending') return '/pending';
  if (me.status === 'paused') return '/paused';
  if (roles && !roles.includes(me.role)) return roleHome(me.role);
  return null;
}

/**
 * Pure guard decision for a capability-gated route. Fails closed: a signed-out
 * visitor goes to login, pending and paused accounts go to their status screens,
 * and an account without the capability (or without a capabilities payload at
 * all) is bounced to its own home rather than let through.
 *
 * The parent surface is allowed for any account with linked kids (isParent) and
 * also for a parent-role account before it has linked anyone, so a fresh parent
 * can still reach their family home to add their first kid.
 */
export function capabilityRedirect(me: SessionUser | null, capability: Capability): string | null {
  if (!me) return '/login';
  if (me.status === 'pending') return '/pending';
  if (me.status === 'paused') return '/paused';
  const caps = me.capabilities;
  const allowed =
    capability === 'build'
      ? Boolean(caps?.canBuild)
      : capability === 'parent'
        ? Boolean(caps?.isParent) || me.role === 'parent'
        : Boolean(caps?.isMentor);
  if (!allowed) return roleHome(me.role);
  return null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<SessionUser | null>(null);
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [storedHat, setStoredHatState] = useState<string | null>(() => readStoredHat());

  const refresh = useCallback(async () => {
    try {
      const res = await api.get<AuthResponse>('/accounts/me');
      setMe(res.user);
      setStatus('authed');
    } catch (err) {
      // 401 (not signed in) is expected; anything else also lands as anon so the
      // app stays usable and the person can try to sign in.
      if (!(err instanceof ApiError) || err.status !== 401) {
        // Keep quiet in the console; the UI simply treats this as signed out.
      }
      setMe(null);
      setStatus('anon');
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(async (input: LoginInput) => {
    const res = await api.post<AuthResponse>('/accounts/login', input);
    setMe(res.user);
    setStatus('authed');
    return res.user;
  }, []);

  const signup = useCallback(async (input: SignupWithAiName) => {
    const res = await api.post<AuthResponse>('/accounts/signup', input);
    setMe(res.user);
    setStatus('authed');
    return res.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/accounts/logout');
    } finally {
      getWs().close();
      setMe(null);
      setStatus('anon');
    }
  }, []);

  const setHat = useCallback((next: Hat) => {
    writeStoredHat(next);
    setStoredHatState(next);
  }, []);

  const capabilities = me?.capabilities ?? null;
  const hats = capabilities?.hats ?? [];
  const hat = me ? resolveActiveHat(hats, me.role, storedHat) : null;

  const value = useMemo<AuthContextValue>(
    () => ({
      me,
      status,
      role: me?.role ?? null,
      capabilities,
      canBuild: capabilities?.canBuild ?? false,
      isParent: capabilities?.isParent ?? false,
      isMentor: capabilities?.isMentor ?? false,
      hats,
      hat,
      setHat,
      login,
      signup,
      logout,
      refresh,
    }),
    // hats/hat are derived from me + storedHat; capabilities from me.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [me, status, storedHat, setHat, login, signup, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Access the auth context. Must be used under an AuthProvider. */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside an AuthProvider');
  return ctx;
}

/** The spinner shown while auth loads, shared by the guards. */
function RouteLoading() {
  return (
    <div className="pt-route-loading" role="status" aria-live="polite">
      <span className="pt-visually-hidden">Loading</span>
    </div>
  );
}

export interface RequireRoleProps {
  /** Roles allowed on this route. Omit to allow any signed-in user. */
  roles?: readonly Role[];
  children: ReactNode;
}

/**
 * Route guard. Shows a spinner while auth loads, redirects signed-out users to
 * login (remembering where they were headed), sends pending and paused users to
 * their status screens, and bounces the wrong role to their own home.
 */
export function RequireRole({ roles, children }: RequireRoleProps) {
  const { me, status } = useAuth();
  const location = useLocation();

  if (status === 'loading') return <RouteLoading />;

  const redirect = guardRedirect(me, roles);
  if (redirect) {
    return <Navigate to={redirect} replace state={{ from: location.pathname + location.search }} />;
  }

  return <>{children}</>;
}

export interface RequireCapabilityProps {
  /** The capability an account must have to see this route. */
  capability: Capability;
  children: ReactNode;
}

/**
 * Route guard for a capability-gated surface. Used so the builder is reachable
 * for any account that can build (students, but also mentors and parents under
 * the My Projects hat), and so each hat's surface checks its own capability.
 */
export function RequireCapability({ capability, children }: RequireCapabilityProps) {
  const { me, status } = useAuth();
  const location = useLocation();

  if (status === 'loading') return <RouteLoading />;

  const redirect = capabilityRedirect(me, capability);
  if (redirect) {
    return <Navigate to={redirect} replace state={{ from: location.pathname + location.search }} />;
  }

  return <>{children}</>;
}
